import { HttpClient } from "@angular/common/http";
import {
    AfterViewChecked,
    Component,
    DestroyRef,
    ElementRef,
    Input,
    OnInit,
    ViewChild,
    inject,
} from "@angular/core";
import { FormControl } from "@angular/forms";
import { MatAutocompleteTrigger } from "@angular/material/autocomplete";
import { Observable, of } from "rxjs";
import { map, switchMap, tap } from "rxjs/operators";
import { environment } from "@environments/environment";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

export interface GraphResponse {
    value: any[];
}

export interface SimpleUser {
    displayName: string;
    upn: string;
    oid: string;
}

@Component({
    selector: "people-picker",
    templateUrl: "./people-picker.component.html",
    styleUrls: ["./people-picker.component.scss"],
})
export class PeoplePickerComponent implements OnInit, AfterViewChecked {
    private destroyRef = inject(DestroyRef);
    @Input() Label: string;
    @Input() Multi: boolean;
    @Input() TagControl: FormControl;
    @Input() Required: boolean;
    @ViewChild(MatAutocompleteTrigger, { static: false })
    MatAutocompleteTrigger: MatAutocompleteTrigger;
    @ViewChild("peoplePickerInput", { static: false })
    peoplePickerInput: ElementRef<HTMLInputElement>;
    InputControl: FormControl = new FormControl("");

    matches: Observable<SimpleUser[]> = of([]);
    selectedUsers: SimpleUser[] = [];
    visible = true;
    selectable = true;
    removable = true;
    inputText: string = "";

    displayFn(user: SimpleUser): string {
        return user ? user.displayName : "";
    }

    remove(user: SimpleUser): void {
        const index = this.selectedUsers.findIndex((u) => u.upn === user.upn);

        if (index >= 0) {
            this.selectedUsers.splice(index, 1);
            this.TagControl.setValue(
                this.selectedUsers && this.selectedUsers.length
                    ? this.selectedUsers
                    : null,
            );
        }
    }

    add(user: SimpleUser): void {
        if (
            this.selectedUsers == null ||
            !this.selectedUsers.find((u) => u.upn === user.upn)
        ) {
            if (!this.Multi) this.selectedUsers = [];
            this.selectedUsers.push(user);
            this.TagControl.setValue(this.selectedUsers);
            this.InputControl.setValue("");
            this.inputText = "";
        }
    }

    constructor(private http: HttpClient) {}

    ngOnInit() {
        this.matches = this.InputControl.valueChanges.pipe(
            map((text) => (typeof text === "string" ? text.trim() : "")),
            map((trimmedText) => ({
                text: trimmedText,
                url: `${environment.udfApiUrl}/api/GraphApiSearch/Users?search=${trimmedText}`,
            })),
            switchMap((req) => {
                if (req.text) {
                    let res = this.http.get<GraphResponse>(req.url);
                    return res;
                } else {
                    let def: GraphResponse = { value: [] };
                    return of(def);
                }
            }),
            map((graphResponse) =>
                graphResponse.value.map(
                    (v) =>
                        ({
                            upn: v.userPrincipalName,
                            displayName: v.displayName,
                            oid: v.id,
                        }) as SimpleUser,
                ),
            ),
        );

        this.TagControl.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((val) => {
                this.selectedUsers = val && val.length ? val : null;
            });
    }

    ngAfterViewChecked(): void {
        this.MatAutocompleteTrigger.optionSelections
            .pipe(
                tap((selection) => this.add(selection.source.value)),
                tap(
                    (selection) =>
                        (this.peoplePickerInput.nativeElement.value = ""),
                ),
            )
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe();
    }
}
